import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../../lib/api';
import { fmtDate, fmtDateTime } from '../../lib/format';
import {
  usePublicBooking, useBookingAction, useAddBookingPhoto, useReportDamage, useSaveSignature,
  bookingPhotoUrl, signatureUrl, BOOKING_STATUS_META, type PublicBookingItem, type RentalPhotoKind,
} from './api';

function tokenFromPath(): string {
  const m = window.location.pathname.match(/\/pujcovna\/m\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

const authHint = (e: ApiError) =>
  e.status === 401 || e.status === 403
    ? 'Přihlas se do Studio OS jako vedoucí/admin (na tomto telefonu) a zkus to znovu.'
    : e.message;

// Foto-protokol: náhledy + přidání fotky (kamera telefonu).
function PhotoSection({ token, kind, photos, title }: {
  token: string;
  kind: RentalPhotoKind;
  photos: { id: string }[];
  title: string;
}) {
  const add = useAddBookingPhoto(token);
  const inputRef = useRef<HTMLInputElement>(null);
  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) add.mutate({ file, kind });
    e.target.value = '';
  };
  return (
    <div className="mt-4 border-t border-zinc-100 dark:border-zinc-800 pt-3">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-zinc-400">{title}</span>
        <button
          onClick={() => inputRef.current?.click()}
          disabled={add.isPending}
          className="rounded-lg border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 text-xs font-medium hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50"
        >
          {add.isPending ? 'Nahrávám…' : '📸 Přidat fotku'}
        </button>
        <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} />
      </div>
      {photos.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {photos.map((p) => (
            <a key={p.id} href={bookingPhotoUrl(token, p.id, false)} target="_blank" rel="noreferrer">
              <img src={bookingPhotoUrl(token, p.id)} alt="Foto stavu" className="h-16 w-16 rounded-lg border border-zinc-200 dark:border-zinc-700 object-cover" loading="lazy" />
            </a>
          ))}
        </div>
      )}
      {add.error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{authHint(add.error as ApiError)}</p>}
    </div>
  );
}

// Hlášení poškození položky — volitelně na konkrétní výrobní kus (zapíše opravu do jeho historie).
function DamageForm({ token, item, onClose }: { token: string; item: PublicBookingItem; onClose: () => void }) {
  const report = useReportDamage(token);
  const [unitId, setUnitId] = useState('');
  const [desc, setDesc] = useState('');
  const submit = () => {
    if (!desc.trim()) return;
    report.mutate(
      { itemId: item.id, unitId: unitId || undefined, description: desc.trim() },
      { onSuccess: onClose },
    );
  };
  return (
    <div className="mt-2 rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-950/20 p-3 space-y-2">
      {item.units.length > 0 && (
        <select value={unitId} onChange={(e) => setUnitId(e.target.value)} className="h-10 w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-2 text-sm">
          <option value="">— který kus? (volitelné) —</option>
          {item.units.map((u) => <option key={u.id} value={u.id}>kus {u.label}</option>)}
        </select>
      )}
      <textarea
        rows={2}
        placeholder="Co je poškozené…"
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm"
      />
      <div className="flex gap-2">
        <button onClick={onClose} className="flex-1 rounded-lg border border-zinc-300 dark:border-zinc-700 py-2 text-sm">Zrušit</button>
        <button onClick={submit} disabled={report.isPending || !desc.trim()} className="flex-1 rounded-lg bg-amber-600 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50">
          {report.isPending ? 'Zapisuji…' : 'Zapsat poškození'}
        </button>
      </div>
      {report.error && <p className="text-xs text-red-600 dark:text-red-400">{authHint(report.error as ApiError)}</p>}
    </div>
  );
}

// Podpis zákazníka prstem — kreslení na canvas (pointer events), uloží se jako PNG.
function SignaturePad({ token, kind, saved, title }: { token: string; kind: RentalPhotoKind; saved: boolean; title: string }) {
  const save = useSaveSignature(token);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c || saved) return;
    // interní rozlišení 2× pro ostrost
    c.width = c.offsetWidth * 2;
    c.height = 160 * 2;
    const ctx = c.getContext('2d')!;
    ctx.scale(2, 2);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1c1c1c';
    let drawing = false;
    const pos = (e: PointerEvent) => {
      const r = c.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const down = (e: PointerEvent) => { drawing = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); c.setPointerCapture(e.pointerId); };
    const move = (e: PointerEvent) => { if (!drawing) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setDirty(true); e.preventDefault(); };
    const up = () => { drawing = false; };
    c.addEventListener('pointerdown', down);
    c.addEventListener('pointermove', move);
    c.addEventListener('pointerup', up);
    return () => { c.removeEventListener('pointerdown', down); c.removeEventListener('pointermove', move); c.removeEventListener('pointerup', up); };
  }, [saved]);

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    setDirty(false);
  };
  const submit = () => {
    const c = canvasRef.current;
    if (!c || !dirty) return;
    save.mutate({ kind, dataUrl: c.toDataURL('image/png') });
  };

  return (
    <div className="mt-4 border-t border-zinc-100 dark:border-zinc-800 pt-3">
      <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-zinc-400">{title}</span>
      {saved ? (
        <img src={signatureUrl(token, kind)} alt="Podpis zákazníka" className="mt-2 h-20 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white object-contain" />
      ) : (
        <>
          <canvas
            ref={canvasRef}
            className="mt-2 h-40 w-full touch-none rounded-xl border border-dashed border-zinc-300 dark:border-zinc-600 bg-white"
            aria-label="Podpisové pole"
          />
          <div className="mt-2 flex gap-2">
            <button onClick={clear} className="flex-1 rounded-lg border border-zinc-300 dark:border-zinc-700 py-2 text-sm">Smazat</button>
            <button onClick={submit} disabled={!dirty || save.isPending} className="flex-1 rounded-lg bg-zinc-900 py-2 text-sm font-medium text-white disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900">
              {save.isPending ? 'Ukládám…' : '✍️ Uložit podpis'}
            </button>
          </div>
          {save.error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{authHint(save.error as ApiError)}</p>}
        </>
      )}
    </div>
  );
}

export default function MobilePickupPage() {
  const token = tokenFromPath();
  const booking = usePublicBooking(token);
  const action = useBookingAction(token);
  const [damageFor, setDamageFor] = useState<string | null>(null);
  const b = booking.data;

  const bigBtn = 'flex w-full items-center justify-center gap-2 rounded-xl px-5 py-5 text-lg font-semibold disabled:opacity-50';
  const pickupPhotos = b?.photos.filter((p) => p.kind === 'pickup') ?? [];
  const returnPhotos = b?.photos.filter((p) => p.kind === 'returned') ?? [];
  // Fáze focení podle stavu: před výdejem dokumentuješ výdej, potom vrácení.
  const photoKind: RentalPhotoKind = b?.status === 'reserved' ? 'pickup' : 'returned';

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100">
      <div className="mx-auto max-w-md px-4 py-8">
        <p className="mb-4 text-center text-xs uppercase tracking-wide text-zinc-400">Půjčovna · výdej / vrácení</p>

        {booking.isLoading && <p className="text-center text-sm text-zinc-500">Načítám…</p>}
        {booking.error && (
          <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-10 text-center">
            <div className="text-3xl">❓</div>
            <h1 className="mt-2 text-lg font-semibold">Rezervace nenalezena</h1>
            <p className="mt-1 text-sm text-zinc-500">{(booking.error as ApiError).message}</p>
          </div>
        )}

        {b && (
          <>
            <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-sm text-zinc-500">{b.code}</span>
                <span className={`rounded px-2 py-0.5 text-xs ${BOOKING_STATUS_META[b.status].badge}`}>{BOOKING_STATUS_META[b.status].label}</span>
              </div>
              <h1 className="mt-1 text-xl font-bold tracking-tight">{b.customerName}</h1>
              <p className="mt-0.5 text-sm text-zinc-500">{fmtDate(b.startDate)} – {fmtDate(b.endDate)}</p>

              <ul className="mt-4 divide-y divide-zinc-100 dark:divide-zinc-800 border-t border-zinc-100 dark:border-zinc-800">
                {b.items.map((it) => (
                  <li key={it.id} className="py-2.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span><span className="font-mono text-xs text-zinc-400">{it.code}</span> {it.name}</span>
                      <span className="text-zinc-500">{it.quantity}×</span>
                    </div>
                    {it.damageNote && (
                      <p className="mt-1 rounded-lg bg-amber-50 dark:bg-amber-950/30 px-2 py-1 text-xs text-amber-700 dark:text-amber-300">⚠ {it.damageNote}</p>
                    )}
                    {(b.status === 'picked_up' || b.status === 'returned') && (
                      damageFor === it.id
                        ? <DamageForm token={token} item={it} onClose={() => setDamageFor(null)} />
                        : !it.damageNote && (
                            <button onClick={() => setDamageFor(it.id)} className="mt-1 text-xs text-zinc-500 underline decoration-dotted hover:text-amber-600">
                              Nahlásit poškození
                            </button>
                          )
                    )}
                  </li>
                ))}
              </ul>
              {b.note && <p className="mt-3 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 p-2 text-xs text-zinc-500">{b.note}</p>}
              {b.pickedUpAt && <p className="mt-3 text-xs text-zinc-400">Vydáno: {fmtDateTime(b.pickedUpAt)}</p>}
              {b.returnedAt && <p className="text-xs text-zinc-400">Vráceno: {fmtDateTime(b.returnedAt)}</p>}

              {/* Foto-protokol: před výdejem stav při výdeji, pak stav při vrácení. */}
              {(b.status === 'reserved' || pickupPhotos.length > 0) && (
                <PhotoSection token={token} kind="pickup" photos={pickupPhotos} title={`Stav při výdeji (${pickupPhotos.length})`} />
              )}
              {photoKind === 'returned' && b.status !== 'cancelled' && (
                <PhotoSection token={token} kind="returned" photos={returnPhotos} title={`Stav při vrácení (${returnPhotos.length})`} />
              )}

              {/* Podpis zákazníka — při výdeji a při vrácení (předávací protokol). */}
              {(b.status === 'reserved' || b.hasPickupSignature) && (
                <SignaturePad token={token} kind="pickup" saved={b.hasPickupSignature} title="Podpis zákazníka — převzetí" />
              )}
              {(b.status === 'picked_up' || (b.status === 'returned' && b.hasReturnSignature)) && (
                <SignaturePad token={token} kind="returned" saved={b.hasReturnSignature} title="Podpis zákazníka — vrácení" />
              )}
            </div>

            <div className="mt-5">
              {b.status === 'reserved' && (
                <button className={`${bigBtn} bg-emerald-600 text-white hover:bg-emerald-700`} disabled={action.isPending} onClick={() => action.mutate('pickup')}>
                  📤 {action.isPending ? 'Vydávám…' : 'Vydat'}
                </button>
              )}
              {b.status === 'picked_up' && (
                <button className={`${bigBtn} bg-sky-600 text-white hover:bg-sky-700`} disabled={action.isPending} onClick={() => action.mutate('return')}>
                  📥 {action.isPending ? 'Přijímám…' : 'Vrátit'}
                </button>
              )}
              {b.status === 'returned' && (
                <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/30 p-5 text-center text-emerald-700 dark:text-emerald-300">✅ Vráceno — hotovo</div>
              )}
              {b.status === 'cancelled' && (
                <div className="rounded-xl bg-zinc-100 dark:bg-zinc-800 p-5 text-center text-zinc-500">Rezervace zrušena</div>
              )}

              {action.error && (
                <p className="mt-3 rounded-lg bg-red-50 dark:bg-red-950/30 p-3 text-center text-sm text-red-700 dark:text-red-300">
                  {authHint(action.error as ApiError)}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
